import { Controller } from "@hotwired/stimulus"

// Drives the résumé Studio's AI INSIGHTS panel: posts the target job title +
// description to the /ats endpoint, then updates the score gauge, keyword
// chips, and suggestion list in place from the JSON response.
export default class extends Controller {
  static targets = ["gauge", "suggestions", "title", "desc", "submit", "status", "keywords", "apply"]
  static values = { url: String, applyUrl: String, missing: Array }

  connect() {
    // NOTE on a stacking-context gap that was deliberately NOT fixed here:
    // .rbf-drawer/.rbf-scrim live inside .rb-studio → .app-main-col
    // (position:relative; z-index:10 — a shared layout element, out of this
    // task's scope). That establishes its own stacking context, so despite
    // the drawer/scrim's higher LOCAL z-index (41/40), the whole
    // .app-main-col subtree paints below the sidebar (position:fixed;
    // z-index:50, a body-level sibling of .app-main-col) — confirmed with
    // elementFromPoint() over the sidebar while the drawer was open: it
    // returned a sidebar child, not .rbf-scrim. The sidebar (280px, left
    // edge) never overlaps the drawer itself (right edge) at any width
    // where the sidebar isn't already off-canvas, so this doesn't block the
    // drawer's own content — it only means the scrim doesn't dim/intercept
    // clicks over that 280px strip.
    //
    // template_modal_controller.js hit the identical wall and fixed it by
    // portaling to document.body. That fix was tried here and reverted:
    // portaling .rbf-drawer moves gauge/suggestions/title/desc/submit/
    // status/keywords/apply OUT of this.element's subtree, and Stimulus
    // targets are resolved by searching the controller element's current
    // DOM subtree — moving the drawer silently breaks all eight
    // (hasXTarget → false), which would take out real scoring/apply
    // functionality to fix a cosmetic/edge click-through on the sidebar.
    // Left as a known follow-up rather than trading a working feature for it.

    // Track the current missing keywords (server-rendered initially, refreshed
    // after each score) so the "Add to Skills" button knows what to apply.
    this._missing = this.hasMissingValue ? this.missingValue : []
    this.toggleApply()

    const params = new URLSearchParams(window.location.search)
    if (params.get("scan") !== "1") return
    if (!this.hasDescTarget || !this.descTarget.value.trim()) return
    if (this._autoScored) return
    this._autoScored = true
    // Strip ?scan=1 so a Turbo back/forward restore or manual refresh doesn't
    // re-fire a (paid) AI scan on a fresh controller instance.
    const url = new URL(window.location.href)
    url.searchParams.delete("scan")
    window.history.replaceState({}, "", url)
    // Every one of this controller's targets lives inside .rbf-drawer, which
    // is hidden at load — scoring into a closed drawer spends a (paid) AI call
    // and changes nothing the user can see. Open it first, through the same
    // openDrawer() the Insights button uses (it tolerates no event, falling
    // back to document.activeElement for the focus-return target).
    this.openDrawer()
    this.score()
  }

  async score(event) {
    event?.preventDefault()
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this.submitTarget.disabled = true
    this.show(this.statusTarget, "Scoring…")
    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ job_target: { title: this.titleTarget.value, description: this.descTarget.value } })
      })
      if (res.status === 403) { this.show(this.statusTarget, "Premium required for AI scoring."); return }
      const data = await res.json()
      if (data && data.ats) {
        this.renderGauge(data.ats.score)
        this.renderKeywords(data.ats.matched || [], data.ats.missing || [])
        this.renderSuggestions(data.ats.suggestions || [])
        this._missing = data.ats.missing || []
        this.toggleApply()
      }
      this.show(this.statusTarget, (data && data.notice) || "Updated.")
    } catch (_e) {
      this.show(this.statusTarget, "Scoring failed — try again.")
    } finally {
      this.submitTarget.disabled = false
    }
  }

  // One-click: append the current missing keywords to the resume's Skills
  // section and re-score (server-side), then reload so the Skills field, live
  // preview, and gauge all reflect the change.
  async applyKeywords() {
    const missing = this._missing || []
    if (!missing.length) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (this.hasApplyTarget) this.applyTarget.disabled = true
    this.show(this.statusTarget, "Adding to Skills…")
    try {
      const res = await fetch(this.applyUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ keywords: missing })
      })
      if (res.status === 403) { this.show(this.statusTarget, "Premium required."); return }
      const data = await res.json()
      if (data && data.ok) { window.location.reload(); return }
      this.show(this.statusTarget, "Couldn't add keywords — try again.")
    } catch (_e) {
      this.show(this.statusTarget, "Couldn't add keywords — try again.")
    } finally {
      if (this.hasApplyTarget) this.applyTarget.disabled = false
    }
  }

  toggleApply() {
    if (this.hasApplyTarget) this.applyTarget.classList.toggle("hidden", !(this._missing && this._missing.length))
  }

  // Opened by the command strip's "Insights" button (data-action="ats-panel#openDrawer").
  // Closed three ways — the header's X, the scrim click, and Escape
  // (data-action="keydown.esc@window->ats-panel#closeDrawer" on the drawer
  // itself) — all of which call #closeDrawer below, so focus return is a
  // single code path rather than three copies that could drift.
  openDrawer(event) {
    // event.currentTarget is the Insights button on a real click; fall back
    // to whatever was focused if this is ever invoked programmatically.
    this._opener = (event && event.currentTarget) || document.activeElement
    this.element.querySelector(".rbf-drawer")?.removeAttribute("hidden")
    this.element.querySelector(".rbf-scrim")?.removeAttribute("hidden")
    // Move focus INTO the drawer (the dialog container itself, tabindex="-1"
    // in the markup) rather than leaving it on the now-covered trigger — a
    // sibling task in this plan shipped a menu that dropped focus to
    // document.body on close; this keeps focus somewhere deliberate on
    // both open and close.
    this.element.querySelector(".rbf-drawer")?.focus()
  }

  closeDrawer() {
    this.element.querySelector(".rbf-drawer")?.setAttribute("hidden", "")
    this.element.querySelector(".rbf-scrim")?.setAttribute("hidden", "")
    this._opener?.focus()
    this._opener = null
  }

  // Update every gauge's number, band label, and bar fill in place. The gauge
  // is an HTML bar (not an SVG arc), so the fill is driven by width.
  //
  // _score_gauge is rendered TWICE — the always-visible command strip
  // (edit.html.erb) and the drawer header (_ats_panel.html.erb) — and both
  // carry data-ats-panel-target="gauge". Iterate this.gaugeTargets (plural),
  // never this.gaugeTarget: the singular resolves to the first match in DOM
  // order only, which left the strip showing the pre-rescore score.
  renderGauge(score) {
    const s = Math.max(0, Math.min(100, Math.round(Number(score) || 0)))
    const label = s >= 80 ? "Strong" : s >= 50 ? "Good" : "Needs work"
    this.gaugeTargets.forEach((gauge) => {
      const num = gauge.querySelector("[data-ats-score]")
      if (num) num.textContent = String(s)
      const arc = gauge.querySelector("[data-ats-arc]")
      if (arc) arc.style.width = `${s}%`
      const band = gauge.querySelector("[data-ats-gauge-label]")
      if (band) band.textContent = label
    })
  }

  // Rebuild the matched/missing keyword chips. Mirrors _ats_keywords.html.erb
  // — same rbf-chiplist/rbf-skill markup, so a rescore doesn't swap the
  // drawer's chips back to the pre-redesign Veridian classes.
  renderKeywords(matched, missing) {
    if (!this.hasKeywordsTarget) return
    const groups = []
    if (matched.length) groups.push(this.chipGroup("Already matched", matched, "rbf-skill rbf-skill--ro"))
    if (missing.length) groups.push(this.chipGroup("Missing", missing, "rbf-skill rbf-skill--ro rbf-skill--missing"))
    this.keywordsTarget.innerHTML = groups.join("")
    this.keywordsTarget.classList.toggle("hidden", groups.length === 0)
  }

  // Rebuild the "what to change" list, hiding it when there are none.
  // Mirrors _ats_panel.html.erb's rbf-dfix <li> (icon span + text).
  renderSuggestions(list) {
    if (!this.hasSuggestionsTarget) return
    this.suggestionsTarget.innerHTML = list.map((s) =>
      `<li><span class="material-symbols-outlined" aria-hidden="true">arrow_right</span>${this.escape(s)}</li>`
    ).join("")
    this.suggestionsTarget.classList.toggle("hidden", list.length === 0)
  }

  chipGroup(heading, items, chipClass) {
    const chips = items.map((kw) => `<li class="${chipClass}">${this.escape(kw)}</li>`).join("")
    return `<div><div class="rbf-dsec-k"><span>${this.escape(heading)}</span><span>${items.length}</span></div>` +
           `<ul class="rbf-chiplist">${chips}</ul></div>`
  }

  escape(str) {
    const el = document.createElement("span")
    el.textContent = String(str)
    return el.innerHTML
  }

  show(el, msg) { if (el) { el.textContent = msg; el.classList.remove("hidden") } }
}
